// @flow

import React, { Suspense } from 'react';
import { Switch } from 'react-router';
import { Route } from 'react-router-dom';
import CssBaseline from '@material-ui/core/CssBaseline';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { I18nextProvider } from 'react-i18next';

import Nav from './component/nav';
import SnackbarNotifications from './component/widget/snackbar-notifications';
import Footer from './component/footer';
import asyncComponent from './component/async-component';
import i18n from './i18n';

import {
  PRIVACY_POLICY_ROUTE,
  wizardPageRoute,
  referralIntakeRoute,
} from './util/web-routes';
import initStore from './init-store';
import theme from './theme';
import history from './util/history';
import { ErrorBoundary } from './util/error-boundary';

const store = initStore();

const AsyncWizard = asyncComponent(() => import('./container/application-wizard-container'));
const AsyncReferralIntakePage = asyncComponent(() => import('./component/referrals/referral-workflow'));
const AsyncPrivacyPolicy = asyncComponent(() => import('./component/privacy-policy'));
const AsyncNotFoundPage = asyncComponent(() => import('./component/not-found'));
const AsyncUnsupportedBrowser = asyncComponent(() => import('./component/unsupported-browser'));

const supportsCors = () => ('withCredentials' in new XMLHttpRequest());

const App = () => (
  <Provider store={store}>
    <MuiThemeProvider theme={theme}>
      <ConnectedRouter history={history}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <I18nextProvider i18n={i18n}>
            <Suspense fallback="loading">
              <div>
                <CssBaseline />
                <div style={{ paddingTop: 40 }} />
                <div style={{ minHeight: '80vh' }}>
                  <Nav />
                  <ErrorBoundary>
                    <Switch>
                      {!supportsCors()
                  && (
                  <Route
                    path="*"
                    component={AsyncUnsupportedBrowser}
                  />
                  )}
                      <Route
                        path={wizardPageRoute()}
                        render={(props: Object) => (
                          <AsyncWizard
                            jobApplicationHash={props.match.params.jobApplicationHash}
                          />
                        )}
                      />
                      <Route
                        path={referralIntakeRoute()}
                        render={(props: Object) => (
                          <AsyncReferralIntakePage
                            employeeHash={props.match.params.employeeHash}
                          />
                        )}
                      />
                      <Route
                        path={PRIVACY_POLICY_ROUTE}
                        render={() => <AsyncPrivacyPolicy />}
                      />
                      <Route component={AsyncNotFoundPage} />
                    </Switch>
                  </ErrorBoundary>
                  <SnackbarNotifications />
                </div>
                <Footer />
              </div>
            </Suspense>
          </I18nextProvider>
        </MuiPickersUtilsProvider>
      </ConnectedRouter>
    </MuiThemeProvider>
  </Provider>
);

export default App;
